import React from 'react';
import { Initiative } from './types';
import { TablePagination, useTablePagination } from '../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../shared/useTableSort';

interface Props {
  initiatives: Initiative[];
  selectedId: string | null;
  onSelect: (init: Initiative) => void;
}

export const InitiativesTable: React.FC<Props> = ({ initiatives, selectedId, onSelect }) => {
  const { sortedItems, sortField, sortDirection, toggleSort } = useTableSort(initiatives);
  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return <span className="badge-pill badge-neutral">Draft</span>;
      case 'SUBMITTED':
        return <span className="badge-pill badge-info">Submitted</span>;
      case 'APPROVED':
        return <span className="badge-pill badge-success">Approved</span>;
      case 'ACTIVE':
        return <span className="badge-pill badge-info">Active</span>;
      case 'ON_HOLD':
        return <span className="badge-pill badge-warning">On Hold</span>;
      case 'UNDER_REVIEW':
        return <span className="badge-pill badge-warning">Under Review</span>;
      case 'COMPLETED':
        return <span className="badge-pill badge-success">Completed</span>;
      case 'CANCELLED':
        return <span className="badge-pill badge-danger">Cancelled</span>;
      default:
        return <span className="badge-pill badge-neutral">{status}</span>;
    }
  };

  const getHealthBadge = (health: string, score: number) => {
    switch (health) {
      case 'ON_TRACK':
        return <span className="badge-pill badge-success">● On Track ({score})</span>;
      case 'AT_RISK':
        return <span className="badge-pill badge-warning">● At Risk ({score})</span>;
      case 'CRITICAL':
        return <span className="badge-pill badge-danger">● Critical ({score})</span>;
      default:
        return <span className="badge-pill badge-neutral">● {health} ({score})</span>;
    }
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="initiative_number" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Code / Number
              </SortableTh>
              <SortableTh field="title" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Initiative Title
              </SortableTh>
              <SortableTh field="initiative_type" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Type
              </SortableTh>
              <SortableTh field="target_department" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Department
              </SortableTh>
              <SortableTh field="status" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Status
              </SortableTh>
              <SortableTh field="health_score" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Health
              </SortableTh>
              <SortableTh field="progress_percentage" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Progress
              </SortableTh>
              <SortableTh field="planned_start_date" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Schedule
              </SortableTh>
              <SortableTh field="actual_cost" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Budget ($)
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {initiatives.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                  No initiatives found.
                </td>
              </tr>
            ) : (
              paginatedItems.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => onSelect(item)}
                  style={{
                    cursor: 'pointer',
                    background: selectedId === item.id ? '#f0f9ff' : 'transparent',
                  }}
                >
                  <td>
                    <span style={{ fontWeight: 600, color: '#0284c7', fontFamily: 'monospace' }}>
                      {item.initiative_number}
                    </span>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>v{item.version}</div>
                  </td>
                  <td style={{ maxWidth: '280px' }}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{item.title}</div>
                    <div style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.description}
                    </div>
                  </td>
                  <td style={{ color: '#475569', fontSize: '12px' }}>
                    {item.initiative_type}
                  </td>
                  <td style={{ color: '#334155', fontSize: '12px' }}>
                    {item.target_department}
                  </td>
                  <td>
                    {getStatusBadge(item.status)}
                  </td>
                  <td>
                    {getHealthBadge(item.health_status, item.health_score)}
                  </td>
                  <td style={{ width: '130px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ flex: 1, height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                        <div
                          style={{
                            height: '100%',
                            width: `${item.progress_percentage}%`,
                            background: item.progress_percentage >= 100 ? '#10b981' : '#0284c7',
                          }}
                        />
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>
                        {item.progress_percentage}%
                      </span>
                    </div>
                  </td>
                  <td style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>
                    {item.planned_start_date} ~ {item.planned_end_date}
                  </td>
                  <td style={{ fontSize: '12px', fontWeight: 500, color: '#334155', whiteSpace: 'nowrap' }}>
                    ${(item.actual_cost || 0).toLocaleString()} / ${(item.estimated_budget || 0).toLocaleString()}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(item);
                      }}
                      className="btn-secondary"
                      style={{ width: 'auto', padding: '4px 10px', fontSize: '11px' }}
                    >
                      Detail
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="initiatives"
      />
    </div>
  );
};
